import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import secureLocalStorage from "react-secure-storage";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { MyContext } from "../../Context/MyProvider";
import { socket } from "../../socket";
import { formatIstDateTime } from "../../utils/datetime";

const TIMELINE_LABELS = {
  requested: "Booking Created",
  searching_provider: "Provider Searching",
  offered: "Provider Searching",
  assigned: "Provider Assigned",
  en_route: "Provider En Route",
  arrived: "Provider Arrived",
  visit_started: "Visit Started",
  visit_completed: "Visit Completed",
  payment_pending: "Payment Pending",
  cancelled_patient: "Cancelled",
  cancelled_provider: "Cancelled by Provider",
  failed: "Failed / Escalated",
  no_show: "No Show",
  report_ready: "Visit Report Ready",
  invoice_ready: "Invoice Generated",
  payment_success: "Payment Completed"
};

export default function HomeCareDetail() {
  const { id } = useParams();
  const { ApiBaseUrl, ImgBaseUrl, mapKey } = useContext(MyContext);
  const Userid = secureLocalStorage.getItem("Userid");
  const [data, setData] = useState(null);
  const [rating, setRating] = useState({
    rating_overall: 5, rating_quality: 5, rating_professionalism: 5, rating_punctuality: 5, review_text: ""
  });
  const [liveLoc, setLiveLoc] = useState(null);

  const { isLoaded } = useLoadScript({ googleMapsApiKey: mapKey || "" });

  const load = () => {
    axios.post(`${ApiBaseUrl}/hc/customer/booking_detail`, { customer_id: Userid, booking_id: id })
      .then((r) => { if (r.data.Result) setData(r.data); });
  };

  useEffect(() => {
    load();
    const handler = (msg) => {
      if (String(msg.booking_id) === String(id)) {
        if (msg.event === "location_update") setLiveLoc({ lat: Number(msg.latitude), lng: Number(msg.longitude) });
        load();
      }
    };
    socket.on(`hc:customer:${Userid}`, handler);
    return () => socket.off(`hc:customer:${Userid}`, handler);
  }, [id, Userid, ApiBaseUrl]);

  const cancel = async () => {
    const reason = prompt("Cancel reason?") || "Cancelled by patient";
    const r = await axios.post(`${ApiBaseUrl}/hc/customer/cancel`, { customer_id: Userid, booking_id: id, reason });
    toast.info(r.data.message + (r.data.fee ? ` (fee ₹${r.data.fee})` : ""));
    load();
  };

  const rate = async () => {
    const r = await axios.post(`${ApiBaseUrl}/hc/customer/rate`, { customer_id: Userid, booking_id: id, ...rating });
    toast.success(r.data.message || "Thanks");
  };

  const downloadInvoice = async () => {
    const r = await axios.post(`${ApiBaseUrl}/hc/customer/invoice_pdf`, { customer_id: Userid, booking_id: id });
    if (r.data.Result && r.data.invoice?.file_path) {
      window.open(`${ApiBaseUrl}/${r.data.invoice.file_path}`, "_blank");
    } else toast.error(r.data.message || "Invoice unavailable");
  };

  const providerPos = useMemo(() => {
    if (liveLoc) return liveLoc;
    if (data?.booking?.provider_lat && data?.booking?.provider_lng) {
      return { lat: Number(data.booking.provider_lat), lng: Number(data.booking.provider_lng) };
    }
    return null;
  }, [liveLoc, data]);

  const patientPos = useMemo(() => {
    if (!data?.booking) return null;
    return { lat: Number(data.booking.latitude), lng: Number(data.booking.longitude) };
  }, [data]);

  if (!data) return <div className="p-10 text-center">Loading...</div>;
  const b = data.booking;
  const showMap = ["assigned", "en_route", "arrived"].includes(b.status) && patientPos;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex justify-between items-start gap-4 mb-6">
        <div>
          <p className="text-sm text-rm-primary">{b.booking_code}</p>
          <h1 className="text-2xl font-bold">{b.service_name}</h1>
          <p className="text-gray-600">{b.category_name}</p>
        </div>
        <span className="bg-orange-100 text-rm-deep px-3 py-1 rounded-full text-sm">{b.status}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-semibold mb-2">Visit details</h3>
          <p>{b.schedule_date} · {b.schedule_time}</p>
          <p className="mt-2 text-sm">{b.address_text}</p>
          <p className="mt-2 font-bold">₹{b.total_amount} · {b.payment_status}</p>
          {b.provider_name && (
            <div className="mt-4 flex items-center gap-3">
              {b.provider_image && <img src={ImgBaseUrl + b.provider_image} alt="" className="w-12 h-12 rounded-full object-cover" />}
              <div>
                <p className="font-medium">{b.provider_name}</p>
                <p className="text-sm text-gray-500">{b.provider_phone} · ★{b.provider_rating}</p>
              </div>
            </div>
          )}
          {b.eta_minutes && ["assigned", "en_route"].includes(b.status) && (
            <p className="mt-3 text-sm font-medium text-green-700">Estimated arrival: ~{b.eta_minutes} min</p>
          )}
        </div>

        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-semibold mb-3">Live timeline</h3>
          <ol className="space-y-2">
            {(data.history || []).map((h) => (
              <li key={h.id} className="text-sm border-l-2 border-[#0d9488] pl-3">
                <b>{TIMELINE_LABELS[h.to_status] || h.to_status}</b>
                <div className="text-gray-500">{formatIstDateTime(h.created_at)}</div>
              </li>
            ))}
            {data.report && (
              <li className="text-sm border-l-2 border-green-500 pl-3"><b>Visit Report Ready</b></li>
            )}
            {data.invoice && (
              <li className="text-sm border-l-2 border-green-500 pl-3"><b>Invoice Generated</b></li>
            )}
            {b.payment_status === "paid" && (
              <li className="text-sm border-l-2 border-green-500 pl-3"><b>Payment Completed</b></li>
            )}
          </ol>
        </div>
      </div>

      {showMap && isLoaded && mapKey && (
        <div className="border rounded-xl overflow-hidden mb-6 h-[320px]">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={providerPos || patientPos}
            zoom={13}
          >
            {patientPos && <Marker position={patientPos} label="P" />}
            {providerPos && <Marker position={providerPos} label="H" />}
          </GoogleMap>
        </div>
      )}

      {data.report && (
        <div className="border rounded-xl p-4 bg-white mb-4">
          <h3 className="font-semibold mb-2">Visit report</h3>
          {(data.report.answers || []).map((a) => (
            <div key={a.id} className="mb-2">
              <p className="text-xs text-gray-500">{a.label}</p>
              {a.field_type === "image" || a.field_type === "attachment" || a.field_type === "signature" ? (
                <img src={a.answer_text?.startsWith("http") ? a.answer_text : ImgBaseUrl + a.answer_text} alt="" className="max-h-40 rounded" />
              ) : (
                <p>{a.answer_text}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {data.invoice && (
        <div className="border rounded-xl p-4 bg-white mb-4 flex justify-between items-center">
          <div>
            <h3 className="font-semibold">Invoice {data.invoice.invoice_no}</h3>
            <p>Amount: ₹{data.invoice.amount}</p>
          </div>
          <button className="bg-rm-primary text-white px-4 py-2 rounded" onClick={downloadInvoice}>Download PDF</button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        {!["visit_completed", "cancelled_patient", "cancelled_provider"].includes(b.status) && (
          <button className="bg-red-600 text-white px-4 py-2 rounded" onClick={cancel}>Cancel booking</button>
        )}
      </div>

      {b.status === "visit_completed" && (
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-semibold mb-2">Rate provider</h3>
          <div className="grid grid-cols-2 gap-3 mb-2">
            {[
              ["rating_overall", "Overall"],
              ["rating_quality", "Quality"],
              ["rating_professionalism", "Professionalism"],
              ["rating_punctuality", "Punctuality"]
            ].map(([k, label]) => (
              <label key={k} className="text-sm">{label}
                <input type="number" min="1" max="5" className="border rounded px-3 py-2 w-full mt-1"
                  value={rating[k]} onChange={(e) => setRating({ ...rating, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <textarea className="border rounded w-full px-3 py-2 mb-2" rows={2} placeholder="Review"
            value={rating.review_text} onChange={(e) => setRating({ ...rating, review_text: e.target.value })} />
          <button className="bg-rm-primary text-white px-4 py-2 rounded" onClick={rate}>Submit rating</button>
        </div>
      )}
    </div>
  );
}
